import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../../config";
import { track } from "../../utils/analytics";
import { SiX, SiYoutube, SiTiktok, SiInstagram, SiGoogle } from "react-icons/si";
import { FiUsers, FiMoreHorizontal, FiArrowLeft, FiCamera, FiUpload, FiCheck } from "react-icons/fi";
import PhotoCropper from "./PhotoCropper";
import { SMS_CONSENT_LANGUAGE } from "../../utils/smsConsent";
import "./onboarding.css";

/**
 * Onboarding → plan → paywall. Modelled on quiz-style app funnels: one-tap
 * questions, the user's own numbers fed back as a personal "clip plan", a
 * proof screen every few questions, a short "building your plan" moment, then
 * the paywall (rendered by the parent after onComplete).
 *
 * Streamers connect Twitch/Kick/YouTube (name + username come from the
 * channel); anyone else types them. The face photo is required — the AI uses
 * it to find and frame the streamer.
 */

// Clip yield used for the plan estimate. Real runs have hit 8 clips from a
// 30-minute VOD (16/hour); this uses half that to stay believable.
const CLIPS_PER_STREAM_HOUR = 8;
const PRO_MONTHLY = 24;

const CONNECT_PLATFORMS = [
  { id: "twitch", label: "Connect Twitch", icon: "/twitch.png" },
  { id: "kick", label: "Connect Kick", icon: "/kick.png" },
  { id: "youtube", label: "Connect YouTube", icon: "/youtube.png" },
];

const ALL_PLATFORMS = [
  { id: "twitch", label: "Twitch", icon: "/twitch.png" },
  { id: "youtube", label: "YouTube", icon: "/youtube.png" },
  { id: "kick", label: "Kick", icon: "/kick.png" },
  { id: "rumble", label: "Rumble", icon: "/rumble.png" },
  { id: "x", label: "X", icon: "/x.png" },
  { id: "ganjing", label: "Ganjing World", icon: "/ganjing.png" },
  { id: "own", label: "OWN", icon: "/own.webp" },
];

const HOURS = [
  { id: "1-2", label: "1–2 hours", sub: "Short sessions", mid: 1.5 },
  { id: "3-5", label: "3–5 hours", sub: "The classic stream", mid: 4 },
  { id: "6+", label: "6+ hours", sub: "Marathon streams", mid: 7 },
];
const DAYS = [
  { id: "1-2", label: "1–2 days a week", mid: 1.5 },
  { id: "3-4", label: "3–4 days a week", mid: 3.5 },
  { id: "5-7", label: "5–7 days a week", mid: 6 },
];
const POSTING_NOW = [
  { id: "0", label: "None yet", mid: 0 },
  { id: "1-3", label: "1–3 a week", mid: 2 },
  { id: "4-10", label: "4–10 a week", mid: 7 },
  { id: "10+", label: "10+ a week", mid: 12 },
];

const METHODS = {
  streamer: [
    { id: "none", emoji: "🚫", label: "I don't clip my streams yet" },
    { id: "self", emoji: "✂️", label: "I edit clips myself" },
    { id: "editor", emoji: "💸", label: "I pay an editor" },
    { id: "fans", emoji: "🙌", label: "Fans or clippers do it" },
  ],
  clipper: [
    { id: "self", emoji: "✂️", label: "I edit clips by hand" },
    { id: "tool", emoji: "🤖", label: "I use another AI tool" },
    { id: "new", emoji: "🌱", label: "I'm just getting started" },
  ],
};

const GOALS = {
  streamer: [
    { id: "followers", emoji: "📈", tag: "GROW", label: "Grow my followers" },
    { id: "viral", emoji: "🔥", tag: "BLOW UP", label: "Go viral" },
    { id: "live", emoji: "🔴", tag: "LIVE", label: "Get more viewers on my live" },
    { id: "money", emoji: "💰", tag: "EARN", label: "Make money from clips" },
  ],
  clipper: [
    { id: "money", emoji: "💰", tag: "EARN", label: "Earn more from clipping" },
    { id: "pages", emoji: "📈", tag: "GROW", label: "Grow my clip pages" },
    { id: "clients", emoji: "🤝", tag: "SCALE", label: "Clip for more streamers" },
    { id: "viral", emoji: "🔥", tag: "BLOW UP", label: "Go viral" },
  ],
};

const STRUGGLES = {
  streamer: [
    { id: "time", emoji: "⏳", label: "No time to edit" },
    { id: "moments", emoji: "🤷", label: "I don't know what will go viral" },
    { id: "consistency", emoji: "📉", label: "I can't post consistently" },
    { id: "cost", emoji: "💸", label: "Editors are too expensive" },
  ],
  clipper: [
    { id: "time", emoji: "⏳", label: "Editing takes forever" },
    { id: "moments", emoji: "🔎", label: "Finding the best moments" },
    { id: "consistency", emoji: "📉", label: "I can't post enough" },
    { id: "late", emoji: "🐢", label: "Other clippers post first" },
  ],
};

const STREAM_TYPES = [
  { id: "gaming", emoji: "🎮", label: "Gaming", sub: "Gameplay, clutch moments, reactions" },
  { id: "talking_head", emoji: "🎙️", label: "Podcast / Talking Head", sub: "Podcasts, interviews, commentary" },
  { id: "irl", emoji: "🌎", label: "IRL / Vlog", sub: "Real-life streaming, events, outdoors" },
  { id: "music", emoji: "🎵", label: "Music", sub: "Live performances, DJ sets, production" },
];

const FOUND_VIA_OPTIONS = [
  { id: "twitter", label: "X / Twitter", Icon: SiX, color: "#fff" },
  { id: "youtube", label: "YouTube", Icon: SiYoutube, color: "#FF0000" },
  { id: "tiktok", label: "TikTok", Icon: SiTiktok, color: "#fff" },
  { id: "instagram", label: "Instagram", Icon: SiInstagram, color: "#E4405F" },
  { id: "google", label: "Google Search", Icon: SiGoogle, color: "#4285F4" },
  { id: "friend", label: "Friend / Word of mouth", Icon: FiUsers, color: "#a1a1aa" },
  { id: "other", label: "Other", Icon: FiMoreHorizontal, color: "#a1a1aa" },
];
const FOUND_VIA_OTHER_MIN = 3;

const pick = (list, id) => list.find((x) => x.id === id);

// The screens, in order, for the answers so far (some depend on earlier ones).
function buildSteps(a) {
  const clipper = a.userType === "clipper";
  const steps = ["intro", "userType"];
  if (clipper) steps.push("name", "username", "platform");
  else if (a.manual) steps.push("connect", "name", "username", "platform");
  else {
    steps.push("connect");
    // Connected their channel → photo first (face tracking + right person on
    // stream), then "try before you buy" on one of their own streams.
    // Their name (not the channel handle) is what hook titles say.
    if (a.platform) steps.push("name", "photo", "contentType", "tryFree");
    // A stream picked (clipped once they start the $1 trial) → ask where to
    // text the clips, inside the flow.
    if (a.platform && (a.freeRunJobId || a.freeRunQueued)) steps.push("phone");
  }
  const earlyPhoto = !clipper && !a.manual && !!a.platform;
  // Clippers aren't asked about the streamer's schedule; the plan uses a
  // typical one (planNumbers defaults: 4h × 3–4 days a week).
  if (!clipper) steps.push("hours", "days");
  steps.push("postingNow", "proofClips", "method");
  if (a.method === "self" || a.method === "editor") steps.push("effort");
  steps.push("proofLive", "goal", "struggle");
  if (!earlyPhoto) steps.push("contentType");
  steps.push("proofResults");
  if (!earlyPhoto) steps.push("photo");
  steps.push("foundVia", "loading", "plan");
  return steps;
}
const NO_PROGRESS = new Set(["intro", "loading", "plan"]);

// Progress survives leaving the page — Twitch/Kick/YouTube connect, switching
// tabs/apps, the back button, a refresh — per user, for a week.
const PROGRESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
function progressKey() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return `onboarding_progress_${u?._id || "anon"}`;
  } catch {
    return "onboarding_progress_anon";
  }
}
function loadProgress() {
  try {
    const p = JSON.parse(localStorage.getItem(progressKey()) || "null");
    if (!p?.a || Date.now() - (p.savedAt || 0) > PROGRESS_TTL_MS) return null;
    return p;
  } catch {
    return null;
  }
}
function saveProgress(p) {
  try { localStorage.setItem(progressKey(), JSON.stringify({ ...p, savedAt: Date.now() })); } catch { /* storage full/blocked */ }
}
function clearProgress() {
  try { localStorage.removeItem(progressKey()); } catch { /* ignore */ }
  try { sessionStorage.removeItem("onboarding_state"); } catch { /* old key */ }
}

// Phone photos can be huge or HEIC. Re-encode to a ≤1280px JPEG in the browser
// (Safari decodes HEIC into <img>), so uploads are small and always a format
// the server/AI can read. Falls back to the original file if anything fails.
function shrinkImage(file) {
  return (async () => {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });
      const scale = Math.min(1, 1280 / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.88));
      return blob && blob.size > 0 ? blob : file;
    } finally {
      URL.revokeObjectURL(url);
    }
  })();
}
// Never let resizing hold up the upload: 5s cap, then send the original.
async function prepareImage(file) {
  try {
    return await Promise.race([
      shrinkImage(file),
      new Promise((resolve) => setTimeout(() => resolve(file), 5000)),
    ]);
  } catch {
    return file;
  }
}

// Numbers for the plan, all from the user's own answers.
function planNumbers(a) {
  const hours = pick(HOURS, a.hours)?.mid || 4;
  const days = pick(DAYS, a.days)?.mid || 3.5;
  const clipsPerWeek = Math.max(1, Math.round(hours * days * CLIPS_PER_STREAM_HOUR));
  const postingNow = pick(POSTING_NOW, a.postingNow)?.mid ?? 0;
  const editHours = a.method === "self" ? Number(a.effort) || 0 : 0;
  const editorMonthly = a.method === "editor" ? Math.round((Number(a.effort) || 0) * 4.33) : 0;
  return { hours, days, clipsPerWeek, postingNow, editHours, editorMonthly, streamHoursWeek: Math.round(hours * days) };
}

// Counts up to `to` once mounted.
function CountUp({ to, duration = 1200, prefix = "", suffix = "" }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setV(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{prefix}{v.toLocaleString()}{suffix}</>;
}

function Option({ emoji, icon, tag, label, sub, selected, onClick }) {
  return (
    <button type="button" className={`ob-opt${selected ? " ob-opt--on" : ""}`} onClick={onClick}>
      {icon && <img src={icon} alt="" className="ob-opt-icon" />}
      {emoji && <span className="ob-opt-emoji" aria-hidden="true">{emoji}</span>}
      <span className="ob-opt-text">
        {tag && <span className="ob-opt-tag">{tag}</span>}
        <span className="ob-opt-label">{label}</span>
        {sub && <span className="ob-opt-sub">{sub}</span>}
      </span>
    </button>
  );
}

// Defined outside the component so inputs inside keep focus while typing.
function Q({ title, sub, children }) {
  return (
    <>
      <h1 className="ob-title">{title}</h1>
      {sub && <p className="ob-sub">{sub}</p>}
      <div className="ob-body">{children}</div>
    </>
  );
}

function Continue({ disabled, onClick, label = "Continue" }) {
  return (
    <div className="ob-footer">
      <button type="button" className="ob-cta" disabled={disabled} onClick={onClick}>{label}</button>
    </div>
  );
}

// "Pick a stream": one of their own streams to clip. Nothing is clipped yet:
// the pick waits for the $1 trial (the paywall shows it), which starts the run
// (routes/freeRun.js). A paying user retrying a failed run starts right away.
// Skips itself when they're not eligible or we can't list their streams.
function ago(iso) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "Today" : d === 1 ? "Yesterday" : d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
}
function hm(sec) {
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
}
const PASTE_RE = /twitch\.tv\/videos\/\d+|youtube\.com\/(watch|live|shorts)|youtu\.be\/|kick\.com\/[\w-]+\/videos\//i;
export function TryFree({ onStarted, onSkip, retry = false, streamType = "", streamerName = "" }) {
  const [vods, setVods] = useState(null);
  const [paste, setPaste] = useState(null); // string when no VODs were found → paste a link
  const [pick, setPick] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    axiosInstance.get("/free-run/vods")
      .then((r) => {
        if (!alive) return;
        if (!r.data?.eligible) { track("free_run_unavailable", { reason: r.data?.reason || "ineligible" }); onSkip(true); return; }
        const list = r.data.vods || [];
        if (!list.length) {
          // No past broadcasts found (or the list failed) → paste a link instead.
          track("free_run_paste_shown", { reason: r.data?.reason || "no_vods" });
          setPaste("");
          return;
        }
        setVods(list);
        setPick(list[0].id);
        track("free_run_shown", { platform: r.data.platform, vods: list.length });
      })
      .catch(() => alive && onSkip(true));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    const pasted = paste !== null;
    if (starting || (pasted ? !PASTE_RE.test(paste) : !pick)) return;
    setStarting(true);
    setError("");
    try {
      const r = await axiosInstance.post("/free-run/start", { ...(pasted ? { videoUrl: paste.trim() } : { videoId: pick }), streamType, streamerName });
      const url = pasted ? paste.trim() : vods?.find((v) => v.id === pick)?.url || null;
      if (r.data?.queued) {
        track("free_run_queued", { retry });
        onStarted(null, url, true);
        return;
      }
      track("free_run_started", { retry });
      onStarted(r.data.jobId, url, false);
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === "used" || code === "channel_used" || code === "subscribed" || code === "region") { onSkip(true); return; }
      setError(err.response?.data?.error && err.response.status === 400 ? err.response.data.error : "Couldn't start that one. Try another stream.");
      setStarting(false);
    }
  };

  return (
    <Q
      title={retry ? "Let's find your clips in another stream" : paste !== null ? "Paste a stream, get your clips" : "Pick a stream, get your clips"}
      sub={retry
        ? "That one didn't have enough clip-worthy moments. Pick a different stream."
        : "Pick the one you want clipped. We'll cut your best moments from it as soon as you unlock Clipbait."}
    >
      {paste !== null ? (
        <>
          <input className="ob-input" autoFocus type="url" inputMode="url" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
            placeholder="twitch.tv/videos/… or a YouTube / Kick link"
            value={paste} onChange={(e) => { setPaste(e.target.value); setError(""); }}
            onKeyDown={(e) => e.key === "Enter" && start()} />
          <p className="ob-vod-hint">We couldn&apos;t find past broadcasts on your channel.</p>
        </>
      ) : !vods ? (
        <div className="ob-vods-loading"><span className="ob-spin" />Finding your streams…</div>
      ) : (
        <div className="ob-vods">
          {vods.map((v) => (
            <button type="button" key={v.id} className={`ob-vod${pick === v.id ? " ob-vod--on" : ""}`} onClick={() => setPick(v.id)}>
              <span className="ob-vod-thumb">
                {v.thumbnail ? <img src={v.thumbnail} alt="" loading="lazy" /> : null}
                {v.durationSec > 0 && <span className="ob-vod-dur">{hm(v.durationSec)}</span>}
              </span>
              <span className="ob-vod-text">
                <span className="ob-vod-title">{v.title}</span>
                <span className="ob-vod-meta">{v.createdAt ? ago(v.createdAt) : ""}{v.views ? ` · ${v.views.toLocaleString()} views` : ""}</span>
              </span>
              <span className="ob-vod-check" aria-hidden="true">{pick === v.id && <FiCheck />}</span>
            </button>
          ))}
        </div>
      )}
      {error && <p className="ob-vod-error">{error}</p>}
      <div className="ob-footer">
        <button type="button" className="ob-cta" disabled={starting || (paste !== null ? !PASTE_RE.test(paste) : !pick)} onClick={start}>
          {starting ? "Saving…" : "Clip this stream ⚡"}
        </button>
      </div>
    </Q>
  );
}

// "Where should we text your clips?" — an onboarding step (not a pop-up) right
// after their clips start. Consent text stays verbatim (TCPA / 10DLC record).
function PhoneStep({ jobId, onDone }) {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    // The home screen won't ask again for this run.
    try { if (jobId) localStorage.setItem(`free_run_phone_asked_${jobId}`, "1"); } catch { /* ignore */ }
  }, [jobId]);
  const save = async () => {
    if (!phone.trim() || !consent || saving) return;
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/sms/phone", { phoneNumber: phone, smsConsent: true, consentLanguage: SMS_CONSENT_LANGUAGE });
      track("onboarding_phone_saved");
      onDone(true);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't save that number. Check it and try again.");
      setSaving(false);
    }
  };
  return (
    <Q title="Where should we text your clips?" sub="We'll text you the second they're ready.">
      <div className="ob-notif" aria-hidden="true">
        <img src="/mascot.png" alt="" />
        <span className="ob-notif-text"><b>Clipbait</b><span>🔥 Your clips are ready. Tap to watch.</span></span>
        <span className="ob-notif-time">now</span>
      </div>
      <input className="ob-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567"
        value={phone} onChange={(e) => { setPhone(e.target.value); setError(""); }}
        onKeyDown={(e) => e.key === "Enter" && save()} />
      <label className="ob-consent">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>Text me clip alerts &amp; offers</span>
      </label>
      <p className="ob-consent-fine">{SMS_CONSENT_LANGUAGE}</p>
      {error && <p className="ob-vod-error">{error}</p>}
      <div className="ob-footer">
        <button type="button" className="ob-cta" disabled={!phone.trim() || !consent || saving} onClick={save}>
          {saving ? "Saving…" : "Text me when they're ready"}
        </button>
        <button type="button" className="ob-link" disabled={saving} onClick={() => { track("onboarding_phone_skipped"); onDone(false); }}>
          Skip
        </button>
      </div>
    </Q>
  );
}

const LOADING_ITEMS = [
  "Reading your stream schedule",
  "Tuning viral-moment detection",
  "Setting up face tracking",
  "Matching your clip style",
  "Building your clip plan",
];

const Onboarding = ({ onComplete }) => {
  const { t } = useTranslation("onboarding");
  const [a, setA] = useState(() => loadProgress()?.a || { userType: null, manual: false, name: "", username: "", platform: "" });
  // Start on the saved step (or the connect step when back from a connect
  // redirect) so the intro never flashes before progress is restored.
  const [stepKey, setStepKey] = useState(() => {
    const p = loadProgress();
    return p?.connectingStream ? "connect" : p?.stepKey || "intro";
  });
  const [dir, setDir] = useState(1);
  const [connecting, setConnecting] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null); // uploaded URL (survives a reload)
  const [crop, setCrop] = useState(null); // { file, src } while the cropper is open
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [foundViaOther, setFoundViaOther] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadPct, setLoadPct] = useState(0);
  const uploadRef = useRef(null);
  const selfieRef = useRef(null);

  const steps = useMemo(() => buildSteps(a), [a]);
  const idx = Math.max(0, steps.indexOf(stepKey));
  const counted = steps.filter((s) => !NO_PROGRESS.has(s));
  const progress = Math.max(0.04, (counted.indexOf(stepKey) + 1) / counted.length);
  const role = a.userType === "clipper" ? "clipper" : "streamer";
  const n = planNumbers(a);
  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

  const goTo = (key, d = 1) => { setDir(d); setStepKey(key); window.scrollTo?.(0, 0); };
  const next = (answers = a) => {
    const s = buildSteps(answers);
    const i = s.indexOf(stepKey);
    if (i >= 0 && i < s.length - 1) goTo(s[i + 1]);
  };
  const back = () => {
    if (stepKey === "name" && a.manual && role === "streamer") {
      setA((p) => ({ ...p, manual: false }));
      return goTo("connect", -1);
    }
    if (idx > 0) goTo(steps[idx - 1], -1);
  };
  // Single-choice answer: highlight, then advance.
  const choose = (field, value) => {
    const answers = { ...a, [field]: value };
    setA(answers);
    setTimeout(() => next(answers), 220);
  };

  useEffect(() => { track("onboarding_step", { step: stepKey }); }, [stepKey]);

  // "Building your plan" — a few seconds, then the plan.
  useEffect(() => {
    if (stepKey !== "loading") return;
    setLoadPct(0);
    // A timer (not requestAnimationFrame) so it still finishes if the tab is
    // backgrounded mid-way.
    const start = Date.now();
    const DURATION = 4200;
    const id = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / DURATION);
      setLoadPct(Math.round(100 * (1 - Math.pow(1 - p, 2))));
      if (p >= 1) {
        clearInterval(id);
        setTimeout(() => goTo("plan"), 350);
      }
    }, 60);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // ── Stream connect (OAuth bounce → back to /home, resumed below) ──
  const connectStream = async (platformId) => {
    setConnecting(platformId);
    track("stream_connect_clicked", { platform: platformId });
    try {
      await axiosInstance.put("/users/me/creator-settings", {}); // creates the brand the callback attaches to
    } catch (err) {
      console.error("Failed to pre-save creator settings:", err);
    }
    saveProgress({ a, stepKey: "connect", photoUrl, photoUploaded, connectingStream: platformId });
    // A short-lived ticket identifies them to the connect route, so it works
    // even when the browser doesn't send the login cookie (iOS Safari).
    let ticket = "";
    try {
      ticket = (await axiosInstance.post("/connect/ticket")).data?.ticket || "";
    } catch (err) {
      console.error("Failed to get connect ticket:", err);
    }
    window.location.href = `${axiosInstance.defaults.baseURL}/connect/${platformId}${ticket ? `?ticket=${encodeURIComponent(ticket)}` : ""}`;
  };

  // Back from (or bailed out of) a Twitch/Kick/YouTube connect: if the channel
  // is connected now, fill it in and move on; otherwise stay on the connect step.
  const resumeConnect = async (platformId) => {
    setConnecting(platformId);
    try {
      const res = await axiosInstance.get("/users/me/creator-settings");
      const conn = res.data?.socialConnections?.[platformId];
      if (conn?.connected) {
        setA((p) => ({
          ...p,
          platform: platformId,
          username: conn.username || "",
          name: "", // asked next — their real name, not the channel handle
          channelAvatar: conn.avatar || null,
        }));
        setConnecting(null);
        goTo("name");
        return;
      }
    } catch (err) {
      console.error("Failed to check stream connection:", err);
    }
    setConnecting(null);
    goTo("connect");
  };

  // Restore saved progress on mount, and again when the page comes back from
  // the browser's back/forward cache (nothing re-mounts in that case).
  useEffect(() => {
    const restore = () => {
      const p = loadProgress();
      if (!p) return;
      setA(p.a);
      if (p.photoUrl) setPhotoUrl(p.photoUrl);
      if (p.photoUploaded) { setPhotoUploaded(true); if (p.photoUrl) setPhotoPreview(p.photoUrl); }
      if (p.connectingStream) {
        setStepKey("connect");
        saveProgress({ ...p, connectingStream: null });
        resumeConnect(p.connectingStream);
      } else if (p.stepKey) {
        setStepKey(p.stepKey);
      }
    };
    restore();
    const onPageShow = (e) => { if (e.persisted) restore(); };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save progress as they go.
  useEffect(() => {
    if (stepKey === "intro" && !a.userType) return;
    saveProgress({ a, stepKey, photoUrl, photoUploaded, connectingStream: null });
  }, [a, stepKey, photoUrl, photoUploaded]);

  // …and on the server once per step, so the admin page has their answers and
  // where they stopped even if they leave before finishing.
  useEffect(() => {
    if (!a.userType) return;
    axiosInstance.put("/users/me/onboarding/progress", {
      userType: a.userType, step: stepKey, foundVia: a.foundVia || undefined,
      answers: { streamHours: a.hours, streamDays: a.days, postingNow: a.postingNow, clipMethod: a.method, effort: a.effort, goal: a.goal, struggle: a.struggle },
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // ── Photo ──
  // Picking / taking a photo opens the cropper first ("Center your face").
  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPhotoError("");
    setCrop({ file, src: URL.createObjectURL(file) });
    track("onboarding_photo_crop_opened");
  };
  const closeCrop = () => {
    if (crop?.src) URL.revokeObjectURL(crop.src);
    setCrop(null);
  };

  // Upload the cropped square (or, if the browser couldn't show the photo for
  // cropping, the original resized).
  const uploadPhoto = async (blobOrFile, { resize = false } = {}) => {
    setPhotoUploaded(false);
    setUploading(true);
    const image = resize ? await prepareImage(blobOrFile) : blobOrFile;
    setPhotoPreview(URL.createObjectURL(image));
    const upload = () => {
      const fd = new FormData();
      fd.append("profilePicture", image, "photo.jpg");
      return axiosInstance.post("/users/me/profile-picture", fd, { timeout: 60000 });
    };
    try {
      let res;
      try {
        res = await upload();
      } catch (err) {
        if (err.response && err.response.status < 500) throw err; // bad file — don't retry
        res = await upload(); // network blip / server hiccup — one retry
      }
      if (res.data?.profilePicture) setPhotoUrl(res.data.profilePicture);
      setPhotoUploaded(true);
      track("onboarding_photo_uploaded");
    } catch (err) {
      track("onboarding_photo_failed", { status: err.response?.status || null, message: err.message });
      setPhotoError(err.response?.data?.error || "Upload didn't go through. Check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  // ── Save + hand off to the paywall ──
  const finish = async () => {
    setSaving(true);
    try {
      const referralSource = localStorage.getItem("referral_via") || "";
      const res = await axiosInstance.post("/users/me/onboarding", {
        userType: a.userType,
        streamerName: a.name.trim(),
        channelUsername: a.username.trim(),
        platform: a.platform,
        streamType: a.contentType || "",
        wantsOpportunities: false,
        foundVia: a.foundVia || null,
        foundViaOther: a.foundVia === "other" ? foundViaOther.trim() : "",
        referralSource,
        answers: {
          streamHours: a.hours, streamDays: a.days, postingNow: a.postingNow,
          clipMethod: a.method, effort: a.effort ? Number(a.effort) : undefined,
          goal: a.goal, struggle: a.struggle, clipsPerWeekEstimate: n.clipsPerWeek,
        },
      });
      if (referralSource) localStorage.removeItem("referral_via");
      track("onboarding_completed", { userType: a.userType, platform: a.platform, foundVia: a.foundVia, goal: a.goal });
      clearProgress();
      onComplete?.({ ...res.data, freeRunJobId: a.freeRunJobId || null, freeRunVideoUrl: a.freeRunVideoUrl || null, photoUrl: photoUrl || null, phoneSaved: !!a.phoneSaved });
    } catch (err) {
      console.error("Failed to save onboarding:", err);
      onComplete?.({ freeRunJobId: a.freeRunJobId || null, freeRunVideoUrl: a.freeRunVideoUrl || null, photoUrl: photoUrl || null });
    } finally {
      setSaving(false);
    }
  };

  // ── Screens ──
  const they = role === "clipper" ? "they" : "you";
  const their = role === "clipper" ? "their" : "your";
  const goalLabel = pick(GOALS[role], a.goal)?.label;
  // "Grow my followers" → "grow your followers" for use mid-sentence.
  const goalPhrase = goalLabel ? goalLabel.toLowerCase().replace(/\bmy\b/g, "your") : null;

  let screen;
  switch (stepKey) {
    case "intro":
      screen = (
        <div className="ob-intro">
          <img src="/mascot.png" alt="Clipbait" className="ob-intro-logo" />
          <h1 className="ob-title ob-title--center">{t("title")}</h1>
          <p className="ob-sub ob-sub--center">{t("subtitle")}</p>
          <p className="ob-facts-head">{t("factHeader")}</p>
          <div className="ob-fact">{t("fact1")}</div>
          <div className="ob-fact">{t("fact2")}</div>
          <Continue onClick={() => goTo("userType")} label={t("letsGetStarted")} />
        </div>
      );
      break;

    case "userType":
      screen = (
        <Q title="Are you a streamer or a clipper?" sub="We'll tailor your plan to how you use Clipbait.">
          <Option emoji="🎥" tag="STREAMER" label="I'm a streamer" sub="Clip my own streams" selected={a.userType === "streamer"} onClick={() => choose("userType", "streamer")} />
          <Option emoji="✂️" tag="CLIPPER" label="I'm a clipper" sub="Clip streams for someone else" selected={a.userType === "clipper"} onClick={() => choose("userType", "clipper")} />
        </Q>
      );
      break;

    case "connect":
      screen = (
        <Q title="Where do you stream?" sub="Connect your channel and Clipbait auto-clips every stream you go live on.">
          {CONNECT_PLATFORMS.map((p) => (
            <Option key={p.id} icon={p.icon} label={connecting === p.id ? "Connecting…" : p.label} onClick={() => !connecting && connectStream(p.id)} />
          ))}
          <button type="button" className="ob-link" disabled={!!connecting} onClick={() => { const ans = { ...a, manual: true }; setA(ans); next(ans); }}>
            I stream somewhere else
          </button>
        </Q>
      );
      break;

    case "tryFree":
      screen = (
        <TryFree
          streamType={a.contentType || ""}
          streamerName={(a.name || "").trim()}
          onStarted={(jobId, url, queued) => { const ans = { ...a, freeRunJobId: jobId || null, freeRunQueued: !!queued, freeRunVideoUrl: url || null }; setA(ans); next(ans); }}
          onSkip={() => next()}
        />
      );
      break;

    case "phone":
      screen = (
        <PhoneStep
          jobId={a.freeRunJobId}
          onDone={(saved) => { const ans = { ...a, phoneSaved: saved }; setA(ans); next(ans); }}
        />
      );
      break;

    case "name":
      screen = (
        <Q
          title={role === "clipper" ? "Which streamer do you clip?" : a.platform && !a.manual ? "What should your clips call you?" : "What's your streamer name?"}
          sub={role !== "clipper" && a.platform && !a.manual
            ? `The name your fans know you by, not your channel handle${a.username ? ` (${a.username})` : ""}. It goes in your hook titles.`
            : "This helps the AI write better hooks and titles for your clips."}
        >
          <input className="ob-input" autoFocus name="cb-display-name" enterKeyHint="next" autoComplete="off" autoCorrect="off" autoCapitalize="words" spellCheck={false}
            data-1p-ignore="true" data-lpignore="true" data-form-type="other"
            placeholder={role === "clipper" ? "e.g. Kai Cenat" : a.platform && !a.manual ? "e.g. Spirit" : "Your display name"}
            value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && a.name.trim() && next()} />
          <Continue onClick={() => next()} disabled={!a.name.trim()} />
        </Q>
      );
      break;

    case "username":
      screen = (
        <Q title={role === "clipper" ? "What's their channel handle?" : "What's your channel handle?"} sub="The name in your channel link. No @ symbol.">
          <input className="ob-input" autoFocus name="cb-channel-handle" type="text" enterKeyHint="next" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
            data-1p-ignore="true" data-lpignore="true" data-form-type="other"
            placeholder={role === "clipper" ? "russellbrand" : "ishowspeed"}
            value={a.username} onChange={(e) => setA({ ...a, username: e.target.value.replace(/[@\s]/g, "") })}
            onKeyDown={(e) => e.key === "Enter" && a.username.trim() && next()} />
          <Continue onClick={() => next()} disabled={!a.username.trim()} />
        </Q>
      );
      break;

    case "platform":
      screen = (
        <Q title={role === "clipper" ? "Where do they stream?" : "Where do you stream?"}>
          {ALL_PLATFORMS.map((p) => (
            <Option key={p.id} icon={p.icon} label={p.label} selected={a.platform === p.id} onClick={() => choose("platform", p.id)} />
          ))}
        </Q>
      );
      break;

    case "hours":
      screen = (
        <Q title={`How long ${role === "clipper" ? "are their" : "are your"} streams?`} sub="On a typical day.">
          {HOURS.map((h) => <Option key={h.id} label={h.label} sub={h.sub} selected={a.hours === h.id} onClick={() => choose("hours", h.id)} />)}
        </Q>
      );
      break;

    case "days":
      screen = (
        <Q title={`How often do ${they} go live?`} sub="This sets how many clips your plan can deliver.">
          {DAYS.map((d) => <Option key={d.id} label={d.label} selected={a.days === d.id} onClick={() => choose("days", d.id)} />)}
        </Q>
      );
      break;

    case "postingNow":
      screen = (
        <Q title={`How many clips ${role === "clipper" ? "do you post of them" : "do you post"} right now?`} sub="Be honest. This is your starting point.">
          {POSTING_NOW.map((p) => <Option key={p.id} label={p.label} selected={a.postingNow === p.id} onClick={() => choose("postingNow", p.id)} />)}
        </Q>
      );
      break;

    case "proofClips": {
      const max = Math.max(n.clipsPerWeek, n.postingNow, 1);
      screen = (
        <>
          <h1 className="ob-title">{their === "your" ? "Your" : "Their"} streams already have the clips. They&apos;re just not getting posted.</h1>
          <div className="ob-card">
            <div className="ob-card-title">
              {role === "clipper" ? "Clips a week from a typical streamer's schedule" : `Clips a week from ${n.streamHoursWeek} hours of streaming`}
            </div>
            <div className="ob-bars">
              <div className="ob-bar-col">
                <div className="ob-bar-track"><div className="ob-bar ob-bar--muted" style={{ "--h": `${Math.max(6, (n.postingNow / max) * 100)}%` }}><span>{n.postingNow}</span></div></div>
                <div className="ob-bar-label">Right now</div>
              </div>
              <div className="ob-bar-col">
                <div className="ob-bar-track"><div className="ob-bar ob-bar--hot" style={{ "--h": `${(n.clipsPerWeek / max) * 100}%` }}><span>≈<CountUp to={n.clipsPerWeek} /></span></div></div>
                <div className="ob-bar-label">With <b>Clipbait</b></div>
              </div>
            </div>
            <p className="ob-card-note">{role === "clipper" ? "Estimated from a typical 4-hour stream, 3–4 days a week." : "Estimated from your stream schedule."} Every stream has hooks, reactions and moments worth posting.</p>
          </div>
          <Continue onClick={() => next()} />
        </>
      );
      break;
    }

    case "method":
      screen = (
        <Q title={role === "clipper" ? "How do you make clips today?" : "How do your streams get clipped today?"}>
          {METHODS[role].map((m) => <Option key={m.id} emoji={m.emoji} label={m.label} selected={a.method === m.id} onClick={() => choose("method", m.id)} />)}
        </Q>
      );
      break;

    case "effort": {
      const money = a.method === "editor";
      screen = (
        <Q
          title={money ? "How much do you pay your editor a week?" : "How many hours a week do you spend editing?"}
          sub={money ? "A rough number is fine. It shows what you could save." : "A rough number is fine. It shows the time you'll get back."}
        >
          <div className="ob-number">
            {money && <span className="ob-number-unit">$</span>}
            <input className="ob-number-input" autoFocus inputMode="numeric" pattern="[0-9]*" placeholder="0"
              style={{ width: `${Math.max(1, String(a.effort || "").length) + 0.4}ch` }}
              value={a.effort || ""} onChange={(e) => setA({ ...a, effort: e.target.value.replace(/\D/g, "").slice(0, 5) })}
              onKeyDown={(e) => e.key === "Enter" && Number(a.effort) > 0 && next()} />
            {!money && <span className="ob-number-unit ob-number-unit--after">hrs</span>}
          </div>
          <Continue onClick={() => next()} disabled={!(Number(a.effort) > 0)} />
        </Q>
      );
      break;
    }

    case "proofLive":
      screen = (
        <>
          <h1 className="ob-title">Other clippers wait for the VOD. Clipbait clips while {they}&apos;re live.</h1>
          <div className="ob-card">
            <div className="ob-lane">
              <div className="ob-lane-name">Clipbait</div>
              <div className="ob-track">
                <div className="ob-track-fill" />
                {[14, 34, 54, 70].map((x, i) => <span key={x} className="ob-chip" style={{ left: `${x}%`, animationDelay: `${0.4 + i * 0.55}s` }}>▶</span>)}
                <span className="ob-playhead"><span>LIVE</span></span>
              </div>
            </div>
            <div className="ob-lane">
              <div className="ob-lane-name ob-lane-name--muted">Other tools</div>
              <div className="ob-track">
                <div className="ob-track-fill ob-track-fill--muted" />
                <div className="ob-track-wait" />
                {[92, 97].map((x, i) => <span key={x} className="ob-chip ob-chip--muted" style={{ left: `${x}%`, animationDelay: `${3.2 + i * 0.3}s` }}>▶</span>)}
              </div>
            </div>
            <div className="ob-axis"><span>Go live</span><span>Stream ends</span><span>Hours later</span></div>
            <p className="ob-card-note">Post while the moment is still trending, not the next day.</p>
          </div>
          <Continue onClick={() => next()} />
        </>
      );
      break;

    case "goal":
      screen = (
        <Q title="What's your goal?" sub="You can change it anytime.">
          {GOALS[role].map((g) => <Option key={g.id} emoji={g.emoji} tag={g.tag} label={g.label} selected={a.goal === g.id} onClick={() => choose("goal", g.id)} />)}
        </Q>
      );
      break;

    case "struggle":
      screen = (
        <Q title="What's holding you back right now?" sub="Knowing this helps us set Clipbait up for you.">
          {STRUGGLES[role].map((s) => <Option key={s.id} emoji={s.emoji} label={s.label} selected={a.struggle === s.id} onClick={() => choose("struggle", s.id)} />)}
        </Q>
      );
      break;

    case "contentType":
      screen = (
        <Q title={`What kind of content do ${they} stream?`} sub="The AI looks for different moments in each.">
          {STREAM_TYPES.map((s) => <Option key={s.id} emoji={s.emoji} label={s.label} sub={s.sub} selected={a.contentType === s.id} onClick={() => choose("contentType", s.id)} />)}
        </Q>
      );
      break;

    case "proofResults":
      screen = (
        <>
          <h1 className="ob-title">You&apos;re in the right place to {goalPhrase || "grow"}.</h1>
          <div className="ob-card ob-card--center">
            <div className="ob-big"><CountUp to={100} duration={1400} suffix="M+" /></div>
            <div className="ob-big-label">views driven by the Clipbait clipping playbook</div>
            <div className="ob-divider" />
            <div className="ob-big ob-big--sm"><CountUp to={75} duration={1400} suffix="M+" /></div>
            <div className="ob-big-label">impressions on Russell Brand&apos;s page, built on the same editing principles now inside the AI</div>
          </div>
          <Continue onClick={() => next()} />
        </>
      );
      break;

    case "photo": {
      const clipper = role === "clipper";
      screen = (
        <>
          <h1 className="ob-title ob-title--white">{clipper ? `Upload a photo of ${a.name || "the streamer"}` : "Clipbait needs to recognize YOU"}</h1>
          <p className="ob-why">
            The AI uses {clipper ? "their" : "your"} face to pick {clipper ? "them" : "you"} out on stream, so clips follow the right person. <b>No photo = worse clips.</b>
          </p>
          <div className="ob-face">
            <div className="ob-face-frame">
              {photoPreview ? <img src={photoPreview} alt="" /> : <span className="ob-face-empty">{clipper ? "🧑" : "🙂"}</span>}
              <span className="ob-face-box" />
              {photoUploaded && <span className="ob-face-ok"><FiCheck /> Face locked in</span>}
            </div>
          </div>
          <input ref={uploadRef} type="file" accept="image/*" hidden onChange={onPhoto} />
          <input ref={selfieRef} type="file" accept="image/*" capture="user" hidden onChange={onPhoto} />
          <div className={`ob-photo-btns${!clipper && isTouch ? " ob-photo-btns--row" : ""}`}>
            {!clipper && isTouch && (
              <button type="button" className="ob-cta ob-cta--secondary" disabled={uploading} onClick={() => selfieRef.current?.click()}>
                <FiCamera /> Take a selfie
              </button>
            )}
            <button type="button" className="ob-cta ob-cta--secondary" disabled={uploading} onClick={() => uploadRef.current?.click()}>
              <FiUpload /> {photoPreview ? "Change photo" : "Upload a photo"}
            </button>
          </div>
          {photoError && <p className="ob-error">{photoError}</p>}
          {/* The photo buttons are the action until there's a photo, so Continue
              only appears once it's uploaded (keeps the screen to one phone height). */}
          {(photoUploaded || uploading) && (
            <Continue onClick={() => next()} disabled={!photoUploaded || uploading} label={uploading ? "Uploading…" : "Continue"} />
          )}
        </>
      );
      break;
    }

    case "foundVia":
      screen = (
        <Q title="Last one: how did you find Clipbait?">
          <div className="ob-grid">
            {FOUND_VIA_OPTIONS.map((o) => (
              <button key={o.id} type="button" className={`ob-opt ob-opt--compact${a.foundVia === o.id ? " ob-opt--on" : ""}`}
                onClick={() => (o.id === "other" ? setA({ ...a, foundVia: "other" }) : choose("foundVia", o.id))}>
                <span className="ob-opt-glyph" style={{ color: o.color }}><o.Icon size={18} /></span>
                <span className="ob-opt-label">{o.label}</span>
              </button>
            ))}
          </div>
          {a.foundVia === "other" && (
            <>
              <input className="ob-input" autoFocus maxLength={100} placeholder="Tell us where" value={foundViaOther} onChange={(e) => setFoundViaOther(e.target.value)} />
              <Continue onClick={() => next()} disabled={foundViaOther.trim().length < FOUND_VIA_OTHER_MIN} />
            </>
          )}
        </Q>
      );
      break;

    case "loading": {
      const done = Math.floor((loadPct / 100) * LOADING_ITEMS.length);
      screen = (
        <div className="ob-loading">
          <div className="ob-loading-pct">{loadPct}%</div>
          <h1 className="ob-title ob-title--center">Building your clip plan</h1>
          <div className="ob-loading-bar"><span style={{ width: `${loadPct}%` }} /></div>
          <div className="ob-card ob-checklist">
            {LOADING_ITEMS.map((t, i) => (
              <div key={t} className={`ob-check${i < done ? " ob-check--done" : i === done ? " ob-check--active" : ""}`}>
                <span className="ob-check-dot">{i < done ? <FiCheck /> : ""}</span>{t}
              </div>
            ))}
          </div>
        </div>
      );
      break;
    }

    case "plan": {
      const cards = [
        { emoji: "🎬", big: <>≈<CountUp to={n.clipsPerWeek} /></>, label: `ready-to-post clips a week from ${their} streams` },
        n.editHours > 0 && { emoji: "⏱️", big: <><CountUp to={n.editHours} /> hrs</>, label: "of editing back every week" },
        n.editorMonthly > PRO_MONTHLY && { emoji: "💰", big: <CountUp to={n.editorMonthly - PRO_MONTHLY} prefix="$" />, label: "a month saved vs. your editor" },
        { emoji: "🔴", big: "Live", label: `clips ready while ${they}'re still streaming` },
      ].filter(Boolean);
      // Two weeks of clips piling up, one bar per day.
      const DAYS_SHOWN = 14;
      const series = Array.from({ length: DAYS_SHOWN }, (_, d) => Math.round((n.clipsPerWeek * (d + 1)) / 7));
      const top = series[series.length - 1] || 1;
      const byDate = new Date(Date.now() + 7 * 864e5).toLocaleDateString("en-US", { month: "long", day: "numeric" });
      screen = (
        <>
          <div className="ob-plan-check"><FiCheck /></div>
          <h1 className="ob-title ob-title--center">Congratulations, your custom clip plan is ready!</h1>
          <p className="ob-plan-kicker">You&apos;ll have ≈{n.clipsPerWeek} clips ready to post:</p>
          <div className="ob-card ob-plan-date">
            <span className="ob-plan-date-icon" aria-hidden="true">📅</span>
            <span>By {byDate}</span>
          </div>
          <div className="ob-card">
            <div className="ob-card-title">Your personalized plan</div>
            <div className="ob-chart">
              <div className="ob-chart-bars">
                <div className="ob-chart-cap" />
                {series.map((v, i) => (
                  <div key={i} className="ob-chart-col">
                    <div className="ob-chart-bar" style={{ "--h": `${Math.max(4, (v / top) * 100)}%`, animationDelay: `${0.1 + i * 0.06}s` }} />
                  </div>
                ))}
              </div>
              <div className="ob-chart-y"><span>{top}</span><span>{Math.round(top / 2)}</span><span>0</span></div>
            </div>
            <div className="ob-chart-x"><span>Day 1</span><span>Day 7</span><span>Day 14</span></div>
            <p className="ob-card-note">Clips ready to post over your next two weeks.</p>
          </div>
          <p className="ob-plan-kicker ob-plan-kicker--lg">Based on your answers, here&apos;s what you get with Clip<span className="ob-accent">bait</span>:</p>
          <div className="ob-plan-cards">
            {cards.map((c, i) => (
              <div key={i} className="ob-plan-card" style={{ animationDelay: `${0.15 + i * 0.15}s` }}>
                <span className="ob-plan-emoji">{c.emoji}</span>
                <div><div className="ob-plan-big">{c.big}</div><div className="ob-plan-label">{c.label}</div></div>
              </div>
            ))}
          </div>
          <h2 className="ob-h2">How you&apos;ll {goalPhrase || "hit your goal"}</h2>
          <div className="ob-steps">
            <div className="ob-step"><span className="ob-step-emoji">📡</span>{role === "clipper" ? "They go live like normal" : "Go live like normal"}</div>
            <div className="ob-step"><span className="ob-step-emoji">⚡</span>Clipbait clips the best moments in real time</div>
            <div className="ob-step"><span className="ob-step-emoji">📲</span>Post 9:16 &amp; 16:9 clips everywhere</div>
          </div>
          <Continue onClick={finish} disabled={saving} label={saving ? "Saving…" : a.freeRunJobId ? "See my clips →" : "Let's get started!"} />
        </>
      );
      break;
    }

    default:
      screen = null;
  }

  return (
    <div className="ob">
      {crop && (
        <PhotoCropper
          src={crop.src}
          onCancel={closeCrop}
          onLoadError={() => { const f = crop.file; closeCrop(); uploadPhoto(f, { resize: true }); }}
          onConfirm={(blob) => { closeCrop(); track("onboarding_photo_cropped"); uploadPhoto(blob); }}
        />
      )}
      {!NO_PROGRESS.has(stepKey) && (
        <div className="ob-top">
          <button type="button" className="ob-back" onClick={back} aria-label="Back"><FiArrowLeft /></button>
          <div className="ob-progress"><span style={{ width: `${progress * 100}%` }} /></div>
        </div>
      )}
      <div key={stepKey} className={`ob-screen ${dir > 0 ? "ob-in-right" : "ob-in-left"}`}>{screen}</div>
    </div>
  );
};

export default Onboarding;
